import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from "react-router-dom";
import { getTags } from "@/helpers/apis";

export const TAGS_QUERY_KEY = ["tags"] as const;

type UseTagsOptions = {
  mode?: "infinite" | "paginated";
  loadTags?: boolean;
};

export const useTags = ({ mode = "infinite", loadTags = false }: UseTagsOptions = {}) => {
  const [searchParams] = useSearchParams();
  const pageSize = parseInt(searchParams.get("pageSize") || "10");
  const currentPage = parseInt(searchParams.get("page") || "1");
  const owners = searchParams.getAll("owners");
  const search = searchParams.get("search") || "";

  const query = useInfiniteQuery({
    queryKey: [...TAGS_QUERY_KEY, currentPage, pageSize, search, ...owners],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const page = mode === "paginated" ? currentPage : pageParam;
      return await getTags({ page, size: pageSize, search, owners });
    },
    getNextPageParam: (lastPage, _allPages) => {
      if (!lastPage) return undefined;
      const totalPages = Math.ceil(lastPage.total / pageSize);
      const currentPageNum = lastPage.page;
      return currentPageNum >= totalPages ? undefined : currentPageNum + 1;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    enabled: mode === "infinite" ? loadTags : true,
    // Keep previous data while fetching new data to prevent loading flash
    placeholderData: keepPreviousData,
  });

  const allTags = query.data?.pages.flatMap(page => page?.items || []) || [];
  const firstPage = query.data?.pages[0];
  const currentPageData = query.data?.pages[query.data?.pages.length - 1];
  const totalPages = firstPage?.pages || 0;

  if (mode === "infinite") {
    return {
      tags: allTags,
      pageSize,
      search,
      total: firstPage?.total || 0,
      totalPages,
      hasNextPage: query.hasNextPage,
      fetchNextPage: query.fetchNextPage,
      isFetchingNextPage: query.isFetchingNextPage,
      // In v5, use isFetching for loading when we have placeholderData, isPending for initial load
      isLoading: query.isPending && !query.isPlaceholderData,
      isFetching: query.isFetching,
    };
  }

  // For paginated mode, only return the current page's data
  return {
    tags: currentPageData?.items || [],
    pageSize,
    page: currentPage,
    search,
    total: currentPageData?.total || 0,
    totalPages: currentPageData?.pages || 0,
    // In v5, use isFetching for loading when we have placeholderData, isPending for initial load
    isLoading: query.isPending && !query.isPlaceholderData,
    isFetching: query.isFetching,
  };
};
